/**
 * Consent-gated loader for the third-party tag libraries.
 *
 * These used to sit in the root layout as `<Script strategy="lazyOnload">`, so
 * every visitor downloaded and evaluated ~270 KiB of vendor JS on every page
 * load, of which Lighthouse measured ~171 KiB as never-executed code — Consent
 * Mode v2 defaults every storage type to `denied` in <head>, so before consent
 * the tags had nothing to do.
 *
 * Now nothing is fetched until the visitor has actually opted in. A visitor who
 * rejects (or never answers) the banner pays zero bytes for either vendor.
 *
 * This also closes a real compliance gap: unlike Google's tags, the Meta Pixel
 * does not honour Google Consent Mode at all, so an eager
 * `fbq('track','PageView')` fires — and sets its cookies — before anyone has
 * agreed to anything. The pixel itself is no longer loaded from here: it was
 * moved into the GTM container on 2026-09-01 and this module's copy removed,
 * so `1035488752536232` is now configured in GTM-PXD7NNSG and nowhere in this
 * repo. The gap is still closed, one level up — the container is loaded behind
 * the same consent gate, so the pixel inside it cannot fire any earlier than
 * it used to.
 *
 * Google side: gtag.js loaded directly, with both the GA4 property and the
 * Google Ads account configured on it, plus the GTM container GTM-PXD7NNSG
 * alongside it. An earlier container (GTM-5DXNTHN4) was removed when the
 * direct tag went in, because a container's GA4 tag and a direct `config` are
 * two separate hits for one pageview. That hazard has not gone away — see
 * `GTM_OWNS_GA4` below, which is how the two are kept from measuring the same
 * property twice.
 *
 * Ordering is safe. `dataLayer` is a plain array that gtag.js drains when it
 * boots, so the inline Consent Mode defaults, the `consent: update` and the
 * `config` calls pushed before this runs are replayed in order once the
 * library arrives.
 */

const GA4_ID = "G-T9HB55JS63";
const GOOGLE_ADS_ID = "AW-18240323392";
const GTM_ID = "GTM-PXD7NNSG";

/**
 * Does GTM-PXD7NNSG carry its own GA4 tag for `GA4_ID`?
 *
 * This is the one thing that cannot be read from here, and it decides whether
 * the property is measured once or twice. A container's GA4 tag and the direct
 * `gtag('config', GA4_ID)` below are two separate hits for the same pageview —
 * which is exactly why the previous container (GTM-5DXNTHN4) was removed when
 * the direct tag went in.
 *
 * Checked on 2026-09-01 by reading the container's own payload
 * (`gtm.js?id=GTM-PXD7NNSG`): it names neither `GA4_ID` nor `GOOGLE_ADS_ID` —
 * the only tag id in it is its own. So there is nothing to double-count today
 * and this stays `false`, with GA4 and Ads configured directly here.
 *
 * Flip it to `true` if a GA4 tag for this property is ever added to the
 * container, and the direct config steps aside. Getting it wrong in that
 * direction inflates a number; getting it wrong in the other direction loses
 * analytics entirely, which is why `false` is the safer default to sit on.
 */
const GTM_OWNS_GA4 = false;

/**
 * `send_to` for the "JelloDucks Form Submit" conversion action, from the Event
 * snippet marketing generated in Google Ads. Account id + conversion label, so
 * it stays tied to the `config` call in loadGoogleTag() below — fired from the
 * contact form's success branch (components/home/contact-cta.tsx).
 */
export const ADS_FORM_SUBMIT_CONVERSION = `${GOOGLE_ADS_ID}/f8RRCOCr8OYcEMD-1PlD`;

// Module-scope guards: onConsent fires on every page load once a consent cookie
// exists, and onChange can fire repeatedly within one page, so both entry
// points can call in more than once per document.
let gtagLoaded = false;
let gtmLoaded = false;

function injectScript(src: string, id: string): void {
  if (document.getElementById(id)) return;
  const script = document.createElement("script");
  script.id = id;
  script.async = true;
  script.src = src;
  document.head.appendChild(script);
}

/**
 * Run `cb` after the window `load` event, on an idle slice of the main thread.
 *
 * For a returning consented visitor, onConsent fires while the page is still
 * hydrating — injecting ~500 KiB of vendor JS right there put 50-115 ms tasks
 * from gtag.js and the pixel in the middle of the load window, which mobile
 * Lighthouse charged against TBT and LCP. This restores the timing the old
 * `<Script strategy="lazyOnload">` setup had (load + requestIdleCallback),
 * now downstream of the consent gate. The 2s timeout keeps the pageview from
 * waiting forever on a page that never goes idle.
 */
function whenIdle(cb: () => void): void {
  const idle = () => {
    if ("requestIdleCallback" in window) {
      window.requestIdleCallback(cb, { timeout: 2000 });
    } else {
      setTimeout(cb, 200);
    }
  };
  if (document.readyState === "complete") idle();
  else window.addEventListener("load", idle, { once: true });
}

/** Google tag (gtag.js) — carries both GA4 and Google Ads. */
function loadGoogleTag(): void {
  if (gtagLoaded) return;
  gtagLoaded = true;

  // `window.gtag` is the inline Consent Mode bootstrap in the site layout — it
  // pushes `arguments` onto dataLayer, which gtag.js replays on boot, so these
  // land in order even though they run before the library arrives. Both
  // accounts ride one gtag.js; the ?id= only names which config bootstraps the
  // loader.
  window.gtag?.("js", new Date());
  if (!GTM_OWNS_GA4) window.gtag?.("config", GA4_ID);
  window.gtag?.("config", GOOGLE_ADS_ID);
  injectScript(
    `https://www.googletagmanager.com/gtag/js?id=${GA4_ID}`,
    "gtag-src",
  );
}

/**
 * Google Tag Manager.
 *
 * Marketing's install sheet asks for the container snippet inline in `<head>`,
 * "as high as possible". It is loaded from here instead, behind the same
 * consent gate as everything else, for the reason the file header gives: a
 * container is a loader for other people's tags, and not all of them honour
 * Consent Mode. That is no longer hypothetical — the Meta Pixel is one of the
 * tags this container now carries, and it ignores Consent Mode completely, so
 * this gate is the only thing standing between it and a visitor who has not
 * answered. Eager in `<head>` it would fetch and run first. What marketing's
 * instruction is really protecting is *ordering*, and that is preserved: the
 * Consent Mode defaults are still the first thing in `<head>`, they are still
 * ahead of every hit, and `dataLayer` replays in order.
 *
 * The body of this is Google's own snippet, minus its `dataLayer` bootstrap —
 * the inline Consent Mode script in the site layout already created the array
 * and is what must own it.
 */
function loadGtmContainer(): void {
  if (gtmLoaded) return;
  gtmLoaded = true;

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ "gtm.start": Date.now(), event: "gtm.js" });
  injectScript(`https://www.googletagmanager.com/gtm.js?id=${GTM_ID}`, "gtm-src");
}

/**
 * Load the tag libraries the visitor has consented to, and only those.
 *
 * Called from the cookie-consent callbacks (onConsent / onChange), which is the
 * single place that knows the current categories. Safe to call repeatedly.
 *
 * Both libraries load for either category, because both carry tags for both:
 * gtag.js has GA4 (analytics) and Google Ads (marketing) on it, and the
 * container has whatever marketing has put in it, the Meta Pixel included.
 * Consent Mode's storage flags, already updated by the caller, are what decide
 * how much a Google tag may actually do with a given category.
 *
 * The pixel is the exception, and it is worth being plain about: it does not
 * read Consent Mode, so once the container is loaded it fires. It used to be
 * loaded here under `if (marketing)`; inside the container it is no longer
 * this module's to gate, and a visitor who consented to analytics only would
 * now get it. That is not a live case — the banner is off and every category
 * is accepted for every visitor (components/cookie-consent/cookie-consent.tsx),
 * so `marketing` is always true — but if the banner is ever restored, that
 * split has to be rebuilt as a consent trigger on the tag in GTM, because
 * there is nothing left on this side to do it with.
 */
export function loadConsentedTags(opts: {
  analytics: boolean;
  marketing: boolean;
}): void {
  if (typeof window === "undefined") return;
  const { analytics, marketing } = opts;
  if (!analytics && !marketing) return;

  // Queued rather than run: see whenIdle. Repeat calls before the idle slice
  // queue extra thunks, but the loaders' own module-scope guards make the
  // duplicates no-ops.
  whenIdle(() => {
    // The container first: anything it is configured to fire should see the
    // consent state and the dataLayer in the same order a head-installed
    // container would.
    loadGtmContainer();
    loadGoogleTag();
  });
}
